// filepath: c:\Users\barto\Desktop\invayt\web-app\src\features\events\components\EventsList.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EventCard } from './EventsCard';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { LayoutGrid, List } from 'lucide-react';
import { CalendarView } from './CalendarView';
import { SponsorsCarousel } from './SponsorsCarousel';
import redirectTo from '../../../utils/redirect';
import { Event, EventWithRelations } from "../../../services/EventService";
import { slugify } from '../../../utils/slugify';
import { CATEGORIES } from '../../../constants/categories';

// Evitar desfase de día por interpretación UTC
function parseLocalDate(dateStr?: string) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const COUNTRY_MAPPINGS: Record<string, string[]> = {
  'Argentina': ['argentina', 'buenos aires', 'cordoba', 'rosario', 'mendoza', 'ar'],
  'Brazil': ['brazil', 'brasil', 'são paulo', 'rio de janeiro', 'salvador', 'brasília', 'br'],
  'Chile': ['chile', 'santiago', 'valparaíso', 'concepción', 'cl'],
  'Colombia': ['colombia', 'bogotá', 'medellín', 'cali', 'barranquilla', 'co'],
  'Mexico': ['mexico', 'méxico', 'ciudad de méxico', 'guadalajara', 'monterrey', 'mx'],
  'Peru': ['peru', 'perú', 'lima', 'arequipa', 'trujillo', 'pe'],
  'Uruguay': ['uruguay', 'montevideo', 'uy'],
  'Venezuela': ['venezuela', 'caracas', 'maracaibo', 'valencia', 've']
};

// Tokens de países (nombres y códigos) para remover en cualquier posición
const COUNTRY_TOKENS = new Set<string>([
  'argentina','brazil','brasil','chile','colombia','mexico','méxico','peru','perú','uruguay','venezuela',
  'ar','br','cl','co','mx','pe','uy','ve'
]);

// Heurística básica para extraer sólo la ciudad desde una dirección libre
function extractCityFromLocation(rawLocation?: string | null): string {
  const location = (rawLocation || '').trim();
  if (!location) return '';

  if (/(^|\b)(online|virtual|remote)(\b|$)/i.test(location)) return 'Online';

  // Particionar por comas: "calle, barrio, ciudad, provincia, país"
  const parts = location.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 1) return location;

  // Remover países en cualquier posición
  let tokens = parts.filter(p => !COUNTRY_TOKENS.has(p.toLowerCase()));

  // Filtrar niveles administrativos y direcciones
  const adminPatterns = [
    /\bprov(incia|\.?)(\b|\s)/i,
    /\bprovince\b/i,
    /\bestado\b/i,
    /\bstate\b/i,
    /\bregion\b/i,
    /\bdepart(amento|ment)\b/i,
    /\bcounty\b/i,
    /\bdistrict\b/i,
    /\bmunicipio\b/i,
    /\bdpto\b/i,
    /\bcomuna\b/i,
    /\bbs\s?-?as\b/i,
  ];
  const addressPatterns = [
    /\bcalle\b/i, /\bavenida\b/i, /\bav\.?\b/i, /\bave\b/i,
    /\bstreet\b/i, /\bst\.?\b/i, /\broad\b/i, /\brd\.?\b/i,
    /#/,
    /\bpiso\b/i, /\bfloor\b/i,
    /\bcp\b/i, /\b\d{4,5}\b/
  ];

  tokens = tokens.filter(t => {
    const lower = t.toLowerCase();
    if (adminPatterns.some(rx => rx.test(lower))) return false;
    if (addressPatterns.some(rx => rx.test(lower))) return false;
    return true;
  });

  // Preferir CABA explícito
  const cabaToken = tokens.find(t => /\bcaba\b|ciudad autónoma/i.test(t));
  if (cabaToken) return cabaToken.toUpperCase() === 'CABA' ? 'CABA' : cabaToken;

  // Desde la derecha, tomar el primer token restante
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    if (token && !/[0-9]/.test(token)) return token;
  }

  // Fallbacks
  return parts[parts.length - 1];
}

export const EventsList: React.FC = () => {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('Categorías');
  const [searchValue, setSearchValue] = useState<string>('');
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [showAllDates, setShowAllDates] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Inicializar categoría desde la URL (solo una vez al montar)
  useEffect(() => {
    const urlCategory = searchParams.get('category');
    if (urlCategory) {
      setSelectedCategory(urlCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Convert events to the format expected by CalendarView
  const formatEventsForCalendar = (eventsList: any[]) => {
    return eventsList.map(event => {
      // Usar la fecha original sin pasar por toISOString (que la convierte a UTC)
      return {
        id: event.id,
        title: event.title,
        description: event.description ?? 'No description available',
        image: event.image_url || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=80&h=80&fit=crop",
        category: event.category || 'Tech',
        date: event.start_date || 'TBD',   // mantener string original YYYY-MM-DD
        time: event.start_time || 'TBD',
        location: event.location || 'TBD',
        eventType: event.type || 'General',
        modality: event.modality || 'online',
        waiting_list: event.waiting_list || false,
      };
    });
  }

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await Event.getUpcoming(35);
        
        if (error) {
          setError(error.message);
          return;
        }
        
        
        
        setEvents(data || []);
      } catch (err) {
        setError("Failed to load events");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEvents();
  }, []);
  
  const handleEventClick = (event: EventWithRelations) => {
    const slug = slugify(event.title);
    redirectTo(`/event/${slug}`);
  };

  const filteredEvents = events.filter(event => {
    const categoryMatch = selectedCategory === 'Categorías' || event.category === selectedCategory;
    return categoryMatch;
  });

  const groupEventsByDate = (events: EventWithRelations[]) => {
    const groupedEvents: Record<string, EventWithRelations[]> = {};
    
    events.forEach(event => {
      if (!event.start_date) return;
      // No usar new Date(event.start_date) directo
      const key = event.start_date; // clave estable
      if (!groupedEvents[key]) groupedEvents[key] = [];
      groupedEvents[key].push(event);
    });
    
    return groupedEvents;
  };

  const toggleDateExpansion = (date: string) => {
    const newExpandedDates = new Set(expandedDates);
    if (newExpandedDates.has(date)) {
      newExpandedDates.delete(date);
    } else {
      newExpandedDates.add(date);
    }
    setExpandedDates(newExpandedDates);
  };

  const renderEvents = (events: EventWithRelations[]) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
        </div>
      );
    }

    if (events.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No upcoming events found.</p>
        </div>
      );
    }

    if (viewMode === 'calendar') {
      return (
        <CalendarView 
          events={formatEventsForCalendar(events)} 
        />
      );
    }

    const groupedEvents = groupEventsByDate(events);
    const dateEntries = Object.entries(groupedEvents);
    const maxDatesToShow = 6;
    const datesToShow = showAllDates ? dateEntries : dateEntries.slice(0, maxDatesToShow);
    return (
      <div className="space-y-8">
        {datesToShow.map(([rawDate, dateEvents]) => {
          const maxEventsPerDate = 5;
          const isDateExpanded = expandedDates.has(rawDate);
          const eventsToShow = isDateExpanded ? dateEvents : dateEvents.slice(0, maxEventsPerDate);
          const hasMoreEventsInDate = dateEvents.length > maxEventsPerDate;

          const dateObj = parseLocalDate(rawDate);
          const displayDate = dateObj
            ? dateObj.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
            : rawDate;
          
          return (
            <div key={rawDate}>
              <h2 className="text-xl font-semibold mb-4">{displayDate}</h2>
              <div className="space-y-4">
                <table className="w-full table-fixed border-separate border-spacing-y-3">
                  <tbody>
                    {eventsToShow.map(event => (
                      <EventCard
                        key={event.id}
                        id={event.id}
                        title={event.title}
                        description={event.description ?? 'No description'}
                        date={(event.start_date ?? '').replace(/-/g, '/')}
                        time={(event.start_time ?? '')}
                        location={extractCityFromLocation(event.location) || ''}
                        organizer={event.hosts?.find(host => host.is_main_host)?.name ?? 'Unknown'}
                        organizerId={event.hosts?.find(host => host.is_main_host)?.user_id ?? ''}
                        category={event.category ?? 'General'}
                        image={event.image_url || ''}
                        organizerAvatar={event.hosts?.find(host => host.is_main_host)?.avatar_url || ''}
                        parent_event_id={event.parent_event_id}
                        onClick={() => handleEventClick(event)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {hasMoreEventsInDate && (
                <div className="flex justify-center mt-4">
                  <Button 
                    variant="link" 
                    onClick={() => toggleDateExpansion(rawDate)}
                    className="text-black hover:text-black-800"
                  >
                    {isDateExpanded 
                      ? `Show less events for ${displayDate}` 
                      : `See ${dateEvents.length - maxEventsPerDate} more events for ${displayDate}`
                    }
                  </Button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* General "See more dates" button */}
        {dateEntries.length > maxDatesToShow && (
          <div className="flex justify-center mt-8">
            <Button 
              variant="outline" 
              onClick={() => setShowAllDates(!showAllDates)}
              className="px-6 py-2"
            >
              {showAllDates 
                ? "Show less dates" 
                : `See ${dateEntries.length - maxDatesToShow} more dates`
              }
            </Button>
          </div>
        )}
      </div>
    );
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      redirectTo(`/search?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <section style={{minHeight: '100%'}} className="relative -mt-20 bg-white rounded-t-3xl shadow-lg z-10">
      <div className="absolute top-[-2rem] left-1/2 transform -translate-x-1/2 w-full px-6" style={{maxWidth: '800px'}}>
        <div className="relative">
          <Input 
            placeholder="Buscar eventos" 
            className="w-full h-12 pr-20"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button 
            onClick={handleSearch} 
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-accent text-white px-6 h-8 hover:bg-accent/90"
          >
            Buscar
          </Button>
        </div>
      </div>
      <div className="container mx-auto px-6 pt-16 pb-12">
        {/* Sponsors Carousel */}
        <SponsorsCarousel />
        {/* Filters */}
        <div className="flex items-center gap-4 mb-8 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
          
          <Select 
            value={selectedCategory} 
            onValueChange={(value) => {
              setSelectedCategory(value);
              const next = new URLSearchParams(searchParams);
              if (value && value !== 'Categorías') {
                next.set('category', value);
              } else {
                next.delete('category');
              }
              setSearchParams(next, { replace: true });
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Categorías">Categorías</SelectItem>
              {CATEGORIES.map(category => (
                <SelectItem key={category.name} value={category.name}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Modalidades eliminadas */}

          <div className="ml-auto hidden md:flex gap-2">
            <Button 
              variant={viewMode === 'list' ? 'outline-selected' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === 'calendar' ? 'outline-selected' : 'outline'}
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Events */}
        {renderEvents(filteredEvents)}
      </div>
    </section>
  );
};